import { styled, TabsProps as MuiTabsProps, tabsClasses, Tabs as MuiTabs, tabClasses, buttonBaseClasses } from "@mui/material"
import { Override } from "@tm/models"
import { forwardRef } from "react"

type TabSizes = {
    size?: "small" | "medium" | "large"
}

export type TabsProps = Override<MuiTabsProps, TabSizes> & {
    layout?: "buttons" | "bordered-bottom"
}

const TabsComponent = forwardRef<HTMLButtonElement, TabsProps>(({ layout, ...props }, ref) => {
    return <MuiTabs {...props} ref={ref} />
})

export const Tabs = styled(TabsComponent, {
    name: "Tabs",
    slot: "root",
    overridesResolver: (_, styles) => styles.root,
})<TabsProps>(({ theme, variant, layout }) => {
    const padding: string = "9px 11px"

    const buttonBackground = theme.overwrites?.components?.button?.primary?.backgroundColor ?? theme.palette.grey[300]
    const buttonBackgroundHover = theme.overwrites?.components?.button?.primary?.hover?.backgroundColor ?? theme.palette.primary.light

    return {
        [`.${tabsClasses.flexContainer}`]: {
            gap: 6,
            height: "100%",
        },

        [`& .${tabsClasses.indicator}`]: {
            backgroundColor: theme.palette.primary.main,
            height: "3px",
        },
        [`&.${tabClasses.root}.${tabClasses.selected}`]: {
            color: "#2e2e2e",
        },
        [`& .${tabsClasses.scrollButtons}.Mui-disabled`]: {
            opacity: theme.opacity?.disabled,
        },

        ...(variant === "standard" && {
            [`& .${tabsClasses.flexContainer}`]: {
                backgroundColor: "#e2e2e2",
                border: "1px solid lightGray",
                borderTopLeftRadius: theme.radius?.default || "4px",
                borderTopRightRadius: theme.radius?.default || "4px",
            },
            [`& .${tabClasses.root}`]: {
                backgroundColor: "#e2e2e2",

                "&:hover": {
                    backgroundColor: "#4d4d4d",
                    color: "white",
                },
            },
            [`& .${tabClasses.selected}`]: {
                backgroundColor: "#ffffff",
                borderRight: "1px solid lightGray",

                "&:hover": {
                    backgroundColor: "#4d4d4d",
                    color: "white",
                },
            },
            [`& .${tabsClasses.flexContainer} :not(:first-child).${tabClasses.selected}`]: {
                borderLeft: "1px solid lightGray",
            },
            [`& .${tabsClasses.indicator}`]: {
                display: "none",
            },
        }),
        ...(layout === "buttons" && {
            minHeight: 0,

            [`.${tabsClasses.flexContainer}`]: {
                gap: 3,
                paddingBottom: 3,
            },
            [`& .${buttonBaseClasses.root}.${tabClasses.root}`]: {
                backgroundColor: buttonBackground,
                color: theme.palette.getContrastText(buttonBackground),
                margin: 0,
                border: "none",
                borderRadius: theme.radius?.default,
                cursor: "pointer",
                padding,

                "&:hover": {
                    backgroundColor: buttonBackgroundHover,
                    color: theme.palette.getContrastText(buttonBackgroundHover),
                },
            },
            [`& .${buttonBaseClasses.root}.${tabClasses.selected}`]: {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.getContrastText(theme.palette.primary.main),
                translate: "0 3px",
            },
            [`& .${tabsClasses.indicator}`]: {
                display: "none",
            },
        }),
    }
})
